// @flow

import { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import type { PersonaType, Persona } from '../typings';
import cinematicRevealVerticalKeyframes from '../../keyframes/cinematicRevealVerticalKeyframes';
import { WORK_AREA_WIDTH, SMALL_DEVICE_HEIGHT } from '../../constants';

import Row from './Forms/Row';
import HeaderText from './Text/HeaderText';
import ContentText from './Text/ContentText';
import HorizontalSeparator from './HorizontalSeparator';
import Button from './Button';
import FormScreenContainer from './ui/FormScreenContainer';
import PaddedContainer from './ui/PaddedContainer';
import FormFieldsContainer from './ui/FormFieldsContainer';

const StyledFormScreenContainer = styled(FormScreenContainer)`
  @media (min-height: ${SMALL_DEVICE_HEIGHT + 1}px) {
    max-height: ${({ deviceHeight }) => deviceHeight}px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  box-sizing: border-box;
  width: ${WORK_AREA_WIDTH}px;
  justify-content: space-between;
  align-items: flex-start;
  background-color: ${props => props.theme.clockInBg};
  overflow: hidden;
  margin: 120px 0;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  margin: 20px 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${props => props.marginTop || 0};
  opacity: 0;
  animation: ${cinematicRevealVerticalKeyframes} 0.4s ease-in-out;
  animation-delay: ${props => props.delay}s;
  animation-fill-mode: forwards;
`;

const ClickableRow = styled(Row)`
  align-items: center;
  cursor: pointer;
  opacity: 0;
  animation: ${cinematicRevealVerticalKeyframes} 0.4s ease-in-out;
  animation-delay: ${props => props.delay}s;
  animation-fill-mode: forwards;

  &:hover {
    background-color: ${props => props.theme.rowHoverColor};
  }
`;

const AnimatedHeaderText = styled.div`
  opacity: 0;
  animation: ${cinematicRevealVerticalKeyframes} 0.4s ease-in-out;
  animation-delay: ${props => props.delay}s;
  animation-fill-mode: forwards;
`;

const RowLabel = styled(ContentText)`
  ${ClickableRow}:active & {
    color: ${props => props.theme.brand};
  }
`;

type Props = {
  group: PersonaType,
  items: Array<Persona>,
  deviceHeight: number,
  back: () => void,
  cancel: () => *,
  proceed: (persona: Persona) => void
};

export default class GroupPicker extends PureComponent<Props> {
  onClick(e: MouseEvent, item: Persona) {
    e.preventDefault();
    this.props.proceed(item);
  }

  render() {
    const { group, items, back, cancel, deviceHeight } = this.props;

    return (
      <StyledFormScreenContainer deviceHeight={deviceHeight}>
        <FormContainer>
          <FormFieldsContainer deviceHeight={deviceHeight}>
            <PaddedContainer>
              <AnimatedHeaderText>
                <HeaderText>Select shift {group}</HeaderText>
              </AnimatedHeaderText>

              <ItemsContainer>
                {items.map((item, index) => (
                  <ClickableRow
                    as="a"
                    key={item.id}
                    delay={0.2 * (index + 1)}
                    borderTop={!index}
                    borderBottom
                    noPaddingHorizontal
                    onClick={e => this.onClick(e, item)}>
                    {item.color && (
                      <RowLabel bold color={item.color}>
                        {item.name}
                      </RowLabel>
                    )}
                    {!item.color && <RowLabel bold>{item.name}</RowLabel>}
                  </ClickableRow>
                ))}
              </ItemsContainer>
            </PaddedContainer>
          </FormFieldsContainer>

          <ButtonContainer marginTop="60px" delay={0.2 * (items.length + 1)}>
            <PaddedContainer>
              <HorizontalSeparator />
              <ButtonContainer marginTop="20px">
                <Button label="Back" buttonTheme="grey" onPress={back} />
              </ButtonContainer>
              <ButtonContainer marginTop="10px">
                <Button label="Cancel" buttonTheme="grey" onPress={cancel} />
              </ButtonContainer>
            </PaddedContainer>
          </ButtonContainer>
        </FormContainer>
      </StyledFormScreenContainer>
    );
  }
}

GroupPicker.propTypes = {
  group: PropTypes.string,
  items: PropTypes.array,
  back: PropTypes.func,
  cancel: PropTypes.func,
  proceed: PropTypes.func
};
